import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession, DEMO_CODES } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  HeartHandshake, Users, DoorOpen, ShieldCheck, CalendarSearch,
  ArrowRight, Compass, KeyRound, Link2, RotateCcw, Info,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1762416964549-c69d31e8c7a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBodWIlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODMzNDMwMzN8MA&ixlib=rb-4.1.0&q=85&w=1600";

const ROLE_CARDS = [
  { id: "volunteer", icon: HeartHandshake, title: "I want to volunteer", desc: "Join The Mill and give some time to your community.", kind: "private" },
  { id: "group", icon: Users, title: "I run or want to run a group", desc: "Lead an activity, class or community group.", kind: "private" },
  { id: "room", icon: DoorOpen, title: "I want to use or book a room", desc: "Request a space for an activity or session.", kind: "private" },
  { id: "staff", icon: ShieldCheck, title: "I am Mill staff or an administrator", desc: "Manage onboarding, activation and requests.", kind: "staff" },
  { id: "activities", icon: CalendarSearch, title: "I'm looking for activities or support", desc: "Browse what's on — no sign-in needed.", kind: "public" },
];

export default function Doorway() {
  const nav = useNavigate();
  const { session, signIn } = useSession();
  const [panel, setPanel] = useState(null); // 'staff' | 'private' | null
  const [code, setCode] = useState("");

  const onCard = (c) => {
    if (c.kind === "public") return nav("/explore");
    if (c.kind === "staff") { setPanel("staff"); setTimeout(() => document.getElementById("entry-panel")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); return; }
    setPanel("private");
    setTimeout(() => document.getElementById("entry-panel")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const staffEnter = () => {
    const role = signIn(code);
    if (role === "centre_admin" || role === "booking") { toast.success("Demo session started (synthetic)."); nav("/staff"); }
    else { toast.error("Use a staff demo code: admin or booking."); }
  };

  const hasVolunteerSession = session?.role === "volunteer" && session?.token;

  return (
    <div className="pb-4">
      {/* Top half — The Mill & our community */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-mill-bg via-mill-bg/95 to-mill-bg/60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-mill-moss">The Mill · People · Places · Practice</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-mill-ink sm:text-5xl lg:text-6xl">
              A place where people make things happen
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-mill-muted">
              Find activities, join in, volunteer, run a group or use a space. The Mill is your local hub for
              community life — everyone is welcome.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button onClick={() => nav("/explore")} className="h-11 gap-2 bg-mill-moss px-6 text-white hover:bg-mill-moss-dark" data-testid="explore-cta">
                <Compass className="h-4 w-4" /> Explore The Mill
              </Button>
              {hasVolunteerSession && (
                <Button variant="outline" className="h-11 gap-2 px-5" onClick={() => nav("/me")} data-testid="resume-workspace">
                  Return to your Mill Card <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lower half — Who are you here as today? */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <section className="py-12" data-testid="role-choice">
          <div className="mb-6">
            <h2 className="font-display text-2xl font-bold tracking-tight text-mill-ink sm:text-3xl">Who are you here as today?</h2>
            <p className="mt-1.5 text-sm text-mill-muted">This is a choice of journey for today — not a permanent identity label. You can come back as someone else anytime.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ROLE_CARDS.map((c) => (
              <button key={c.id} onClick={() => onCard(c)} data-testid={`role-card-${c.id}`}
                className="group flex items-start gap-4 rounded-2xl border border-stone-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-mill-moss/40 hover:shadow-sm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-mill-alt text-mill-moss transition-colors group-hover:bg-mill-moss group-hover:text-white">
                  <c.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-display font-semibold text-mill-ink">{c.title}</span>
                  <span className="mt-0.5 block text-sm text-mill-muted">{c.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Entry panel */}
        {panel && (
          <section id="entry-panel" className="pb-14">
            {panel === "staff" ? (
              <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="staff-entry">
                <div className="flex items-center gap-2 text-mill-moss"><KeyRound className="h-5 w-5" /><h3 className="font-display text-lg font-semibold text-mill-ink">Staff / administrator access</h3></div>
                <p className="mt-1 text-sm text-mill-muted">Enter a demo access code to open the staff workspace.</p>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs text-mill-muted">Demo access code</label>
                    <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && staffEnter()} placeholder="admin or booking" className="w-56" data-testid="staff-code-input" />
                  </div>
                  <Button className="h-10 bg-mill-moss hover:bg-mill-moss-dark" onClick={staffEnter} data-testid="staff-code-submit">Enter workspace</Button>
                </div>
                <DemoCodeNote />
              </div>
            ) : (
              <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="private-entry">
                <div className="flex items-center gap-2 text-mill-moss"><Link2 className="h-5 w-5" /><h3 className="font-display text-lg font-semibold text-mill-ink">Create your Mill Card</h3></div>
                <p className="mt-2 text-sm leading-relaxed text-mill-muted">
                  The Mill sets up your private Mill Card from a personal invitation. If a member of Mill staff has sent you
                  a link, open it to begin your onboarding. Your role is chosen with you — you don't pick it here.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {hasVolunteerSession && (
                    <Button className="h-10 gap-2 bg-mill-moss hover:bg-mill-moss-dark" onClick={() => nav("/me")} data-testid="private-resume">
                      Return to your Mill Card <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="outline" className="h-10 gap-2" onClick={() => nav("/explore")}>Browse activities first</Button>
                </div>
                <p className="mt-4 flex items-start gap-2 rounded-xl bg-mill-alt/60 px-3 py-2.5 text-xs text-mill-muted" data-testid="private-hint">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Trying the sandbox? A Mill administrator can issue you an invitation from the staff workspace, then open its link to start.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Subtle secondary routes */}
        <section className="border-t border-stone-200 py-6 text-sm text-mill-muted">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="/overview" data-testid="link-overview" className="hover:text-mill-ink">About the design & future model</a>
            <a href="/reference" data-testid="link-reference" className="hover:text-mill-ink">Reference & parked journeys</a>
          </div>
        </section>
      </div>
    </div>
  );
}

export function DemoCodeNote() {
  return (
    <div className="mt-5 rounded-xl border border-dashed border-stone-300 bg-mill-alt/40 p-3.5" data-testid="demo-code-note">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#B45309]">Demo access code — synthetic sandbox, not real authentication</p>
      <p className="mt-1 text-xs text-mill-muted">
        Codes select a demonstration persona and journey only. They are not secure identity verification or permanent authority.
        In a real pilot these are replaced by one-time links / OTP and governed activation.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {DEMO_CODES.map((d) => (
          <span key={d.code} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-mill-ink"><code className="font-mono text-mill-moss">{d.code}</code> — {d.label}</span>
        ))}
      </div>
    </div>
  );
}
